import { ApiError } from './errors'

/** The only place this app talks to the network. */

const BASE = '/api'

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface RequestOptions {
  method?: Method
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
}

/** The CSRF token, held in a module variable rather than in state. */
let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

export function getCsrfToken(): string | null {
  return csrfToken
}

/** Global 401 listener. */
type AuthEvent = 'expired' | 'password-change-required'
const authListeners = new Set<(event: AuthEvent) => void>()

export function onAuthEvent(listener: (event: AuthEvent) => void): () => void {
  authListeners.add(listener)
  return () => authListeners.delete(listener)
}

function emitAuthEvent(event: AuthEvent): void {
  for (const listener of authListeners) listener(event)
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return `${BASE}${path}`
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    // A missing filter must be absent from the URL, not present as "undefined".
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${BASE}${path}?${qs}` : `${BASE}${path}`
}

const MUTATIONS: ReadonlySet<string> = new Set(['POST', 'PATCH', 'PUT', 'DELETE'])

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts
  const headers: Record<string, string> = { Accept: 'application/json' }

  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (MUTATIONS.has(method) && csrfToken) headers['X-CSRF-Token'] = csrfToken

  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      credentials: 'same-origin',
      // Never let a browser or proxy serve a cached lead list. A stale queue.
      cache: 'no-store',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    // An aborted request is the caller navigating away, not a failure.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw ApiError.offline()
  }

  if (!res.ok) {
    const error = await ApiError.fromResponse(res)
    if (error.isUnauthenticated) emitAuthEvent('expired')
    else if (error.needsPasswordChange) emitAuthEvent('password-change-required')
    throw error
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** A CSV or any other non-JSON download. */
export async function apiDownload(
  path: string,
  query?: RequestOptions['query'],
): Promise<{ blob: Blob; truncated: boolean; filename: string }> {
  let res: Response
  try {
    res = await fetch(buildUrl(path, query), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/csv' },
    })
  } catch {
    throw ApiError.offline()
  }

  if (!res.ok) {
    const error = await ApiError.fromResponse(res)
    if (error.isUnauthenticated) emitAuthEvent('expired')
    throw error
  }

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)

  return {
    blob: await res.blob(),
    truncated: res.headers.get('X-Export-Truncated') === 'true',
    filename: match?.[1] ?? 'export.csv',
  }
}
